"use client"

import { useEffect, useState, useCallback } from "react"
import { createPortal } from "react-dom"
import { X } from "lucide-react"
import { Z } from "@/lib/z-index"
import "./glass-toast.less"

type ToastPosition = "bottom-center" | "bottom-left" | "bottom-right" | "top-center" | "top-left" | "top-right"

export interface GlassToastProps {
  /** Whether the toast is visible */
  open: boolean
  /** Called when toast should close */
  onOpenChange: (open: boolean) => void
  /** Auto-dismiss after ms (0 = no auto-dismiss, default 0) */
  duration?: number
  /**
   * Position on screen. When provided, the toast renders as a fixed-position portal.
   * When omitted, it renders inline (for use inside a positioned container).
   */
  position?: ToastPosition
  /** Additional class on the toast container */
  className?: string
  children: React.ReactNode
}

/**
 * GlassToast — A Liquid Glass notification toast with slide-in animation.
 *
 * Two modes:
 * 1. With `position` — self-positioning via fixed portal (standalone usage)
 * 2. Without `position` — inline flow child (for use inside ToastProvider container)
 */
export function GlassToast({
  open,
  onOpenChange,
  duration = 0,
  position,
  className = "",
  children,
}: GlassToastProps) {
  const [mounted, setMounted] = useState(false)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (open) {
      setMounted(true)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true))
      })
    } else if (mounted) {
      setVisible(false)
      const t = setTimeout(() => setMounted(false), 350)
      return () => clearTimeout(t)
    }
  }, [open])

  useEffect(() => {
    if (open && duration > 0) {
      const t = setTimeout(() => onOpenChange(false), duration)
      return () => clearTimeout(t)
    }
  }, [open, duration, onOpenChange])

  const handleDismiss = useCallback(() => {
    onOpenChange(false)
  }, [onOpenChange])

  if (!mounted) return null

  // Standalone mode: self-positioning via fixed portal
  if (position) {
    if (typeof document === "undefined") return null

    const positionStyles = getPositionStyles(position, visible)

    return createPortal(
      <div
        className={`glass-toast ${className}`}
        style={{
          ...positionStyles,
          opacity: visible ? 1 : 0,
          transition: visible
            ? "transform 450ms cubic-bezier(0.22, 1, 0.36, 1), opacity 300ms ease-out"
            : "transform 300ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms ease-in",
        }}
        role="alert"
        aria-live="polite"
      >
        <div className="glass-toast__inner">
          <div className="glass-toast__content">{children}</div>
          <button onClick={handleDismiss} className="glass-toast__dismiss" aria-label="关闭">
            <X className="h-3 w-3" />
          </button>
        </div>
      </div>,
      document.body
    )
  }

  // Container mode: inline flow child
  return (
    <div
      className={`glass-toast ${className}`}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(-100%)",
        transition: visible
          ? "transform 450ms cubic-bezier(0.22, 1, 0.36, 1), opacity 300ms ease-out"
          : "transform 300ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms ease-in",
        pointerEvents: "auto",
      }}
      role="alert"
      aria-live="polite"
    >
      <div className="glass-toast__inner">
        <div className="glass-toast__content">{children}</div>
        <button onClick={handleDismiss} className="glass-toast__dismiss" aria-label="关闭">
          <X className="h-3 w-3" />
        </button>
      </div>
    </div>
  )
}

function getPositionStyles(position: ToastPosition, visible: boolean): React.CSSProperties {
  const base: React.CSSProperties = { position: "fixed", zIndex: Z.TOAST }
  switch (position) {
    case "top-left":
      return { ...base, top: "1.5rem", left: "1.5rem", transform: visible ? "translateY(0)" : "translateY(-120%)" }
    case "top-right":
      return { ...base, top: "1.5rem", right: "1.5rem", transform: visible ? "translateY(0)" : "translateY(-120%)" }
    case "top-center":
      return { ...base, top: "1.5rem", left: "50%", transform: visible ? "translateX(-50%) translateY(0)" : "translateX(-50%) translateY(-120%)" }
    case "bottom-left":
      return { ...base, bottom: "1.5rem", left: "1.5rem", transform: visible ? "translateY(0)" : "translateY(120%)" }
    case "bottom-right":
      return { ...base, bottom: "1.5rem", right: "1.5rem", transform: visible ? "translateY(0)" : "translateY(120%)" }
    default:
      return { ...base, bottom: "1.5rem", left: "50%", transform: visible ? "translateX(-50%) translateY(0)" : "translateX(-50%) translateY(120%)" }
  }
}
